import React from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import {
  type RouteObject,
  Outlet,
  useParams,
  useLocation,
  Navigate,
  type NavigateProps,
  type Location as RRLocation,
  type Params,
  redirect,
  generatePath,
  useRouteError,
} from "react-router"
import { routeNames } from "src/router/route-names"
import LandingScreen from "src/components/landing-screen"
import { useIntl } from "react-intl"
import AuthenticatedRoute from "src/components/authenticated-route"
import { CONFIRM_FIELD_TYPES } from "src/constants"
import ConfirmNewEmailExpired from "src/pages/confirm-new-email/confirm-new-email-expired"
import InPersonFallback from "src/pages/in-person/components/in-person-fallback"
import { isSessionValid } from "@myvp/shared/src/functions/is-session-valid"
import SmartBanner from "src/components/smart-banner"
import { type EntryRouteHandle } from "src/pages/entry/entry"
import { createStudyRoutes } from "src/pages/entry/study/study-routes"
import studyRouteNames from "src/pages/entry/study/study-route-names"
import {
  GLOBAL_DOMAIN,
  DO_NOT_TRANSLATE_CONSTANTS,
} from "@myvp/shared/src/constants"
import ForgotPasswordErrorBoundary from "src/pages/forgot-password/forgot-password-error-boundary"
import { createEconsentPdfRoutes } from "src/pages/econsent-pdf/econsent-pdf.routes"
import { type NestedRouteHandle } from "@myvp/shared/src/functions/get-default-back-path"
import PatientsDevTools from "src/components/patients-dev-tools"

const RootErrorHandler = () => {
  const error = useRouteError()
  // oxlint-disable-next-line no-console
  console.error(error)
  // avoid rendering the debug overlay react-router has
  return <React.Fragment />
}
export const createRootRoute = (routes: RouteObject[]): RouteObject => ({
  path: "/",
  element: (
    <>
      {
        // tree shake this out of release bundles
        process.env.REACT_APP_release !== "true" &&
        (window.location.hostname.includes(".myveevatrials.com") ||
          window.location.hostname === "patients-us.local.com") ? (
          <PatientsDevTools />
        ) : null
      }
      <DocumentHead />
      <Outlet />
    </>
  ),
  errorElement: <RootErrorHandler />,
  children: routes,
  HydrateFallback: () => null,
})

const NavigateWithParams = (props: { to: string }) => {
  const { search } = useLocation()
  const params = useParams()
  const pathname = generatePath(props.to, params)

  return <Navigate replace to={{ pathname, search }} />
}

const createRedirect = (
  args: { from: string; to: string } | { index: true; to: string }
): RouteObject => ({
  path: "from" in args ? args.from : undefined,
  index: "index" in args ? args.index : undefined,
  element: <NavigateWithParams to={args.to} />,
})

/**
 * Mutates or adds html elements to the html document's head element.
 */
const DocumentHead = (): null => {
  const hostname = window.location?.hostname?.split?.(".")?.[0] ?? ""
  const { formatMessage } = useIntl()
  const location = useLocation()

  // set document title
  React.useEffect(() => {
    if (location.pathname === routeNames.econsentEditor()) {
      document.title = DO_NOT_TRANSLATE_CONSTANTS.veevaEconsent
    } else {
      document.title = DO_NOT_TRANSLATE_CONSTANTS.myveevaforpatients
    }
  }, [formatMessage, location.pathname])

  // add meta tag to prevent search indexing
  React.useEffect(() => {
    if (hostname !== GLOBAL_DOMAIN) {
      if (document.head.querySelector('meta[name="robots"]')) return
      const meta = document.createElement("meta")
      meta.name = "robots"
      meta.content = "noindex"
      document.head.appendChild(meta)
    }
  }, [hostname])

  return null
}

const NavigateWithLocation = (props: {
  props: (loc: RRLocation, params: Readonly<Params<string>>) => NavigateProps
}) => {
  const location = useLocation()
  const params = useParams()
  const navigateProps = props.props(location, params)
  return <Navigate {...navigateProps} />
}

const Help = (): null => {
  React.useEffect(() => {
    if (window.location.pathname !== "/help/404.html") {
      window.location.assign("/help/404.html")
    }
  }, [])
  return null
}

const Tos = (): null => {
  React.useEffect(() => {
    // This only runs if a user somehow puts /terms/somethingelse then it redirects back to /terms
    const url = new URL(window.location.href)
    url.pathname = routeNames.termsOfService()
    window.location.assign(url)
  }, [])
  return null
}

const Pn = (): null => {
  React.useEffect(() => {
    // This only runs if a user somehow puts /privacy/somethingelse then it redirects back to /privacy
    const url = new URL(window.location.href)
    url.pathname = routeNames.privacyNotice()
    window.location.assign(url)
  }, [])
  return null
}

function Suspense(props: Partial<React.SuspenseProps>) {
  return (
    <React.Suspense fallback={props.fallback ?? <Fallback />}>
      {props.children}
    </React.Suspense>
  )
}
const Fallback = styled.div`
  height: 100%;
  ${(props) =>
    props.color === "white" &&
    css`
      background: ${props.theme.palette.surface.neutral.default};
    `};
  ${(props) =>
    props.color === "snow" &&
    css`
      background: ${props.theme.palette.background.neutral.default};
    `};
`

const LazyLogin = React.lazy(() => import("src/pages/login"))
const Login = () => (
  <Suspense fallback={<LandingScreen />}>
    <LazyLogin />
  </Suspense>
)
const LazyResetSvUserPassword = React.lazy(
  () => import("src/pages/reset-sv-user-password/reset-sv-user-password")
)
const ResetSvUserPassword = () => (
  <Suspense fallback={<LandingScreen />}>
    <LazyResetSvUserPassword />
  </Suspense>
)

const LazyEnterMfa = React.lazy(() => import("src/pages/enter-mfa"))
const EnterMfa = () => (
  <Suspense fallback={<LandingScreen />}>
    <LazyEnterMfa />
  </Suspense>
)

const LazyEconsentViewer = React.lazy(
  () => import("src/pages/econsent/econsent-viewer/econsent-viewer")
)
const EconsentViewer: React.FC<{ inPerson?: boolean }> = (props) => (
  <Suspense>
    <LazyEconsentViewer inPerson={props.inPerson} />
  </Suspense>
)

const LazyEconsentGenerate = React.lazy(
  () => import("src/pages/econsent/econsent-generate/econsent-generate")
)
const EconsentGenerate = () => (
  <Suspense fallback={null}>
    <LazyEconsentGenerate />
  </Suspense>
)

const LazyEconsentEditor = React.lazy(
  () => import("src/pages/econsent/econsent-editor/econsent-editor")
)
const EconsentEditor = () => (
  <Suspense fallback={null}>
    <LazyEconsentEditor />
  </Suspense>
)

const LazyDetermineUser = React.lazy(() => import("src/pages/determine-user"))
const DetermineUser = () => (
  <Suspense fallback={null}>
    <LazyDetermineUser />
  </Suspense>
)

const LazyEconsentPreviewGenerator = React.lazy(
  () => import("src/pages/econsent/econsent-viewer/econsent-preview-generator")
)
const EconsentPreviewGenerator = () => (
  <Suspense fallback={null}>
    <LazyEconsentPreviewGenerator />
  </Suspense>
)

const LazyError = React.lazy(() => import("src/pages/error"))
const Error = () => (
  <Suspense fallback={null}>
    <LazyError />
  </Suspense>
)

const LazyEntry = React.lazy(() => import("src/pages/entry/entry"))
const Entry = () => (
  <Suspense fallback={null}>
    <LazyEntry>
      <Outlet />
    </LazyEntry>
  </Suspense>
)

const LazyInPerson = React.lazy(() => import("src/pages/in-person/in-person"))
const InPerson = () => (
  <Suspense fallback={null}>
    <LazyInPerson />
  </Suspense>
)
const LazyInPersonConfirm = React.lazy(
  () => import("src/pages/in-person/in-person-confirm")
)
const InPersonConfirm = () => (
  <Suspense fallback={null}>
    <LazyInPersonConfirm />
  </Suspense>
)
const LazyInPersonRoles = React.lazy(
  () => import("src/pages/in-person/in-person-roles")
)
const InPersonRoles = () => (
  <Suspense fallback={null}>
    <LazyInPersonRoles />
  </Suspense>
)

const LazyInPersonSuccess = React.lazy(
  () => import("src/pages/in-person/in-person-success")
)
const InPersonSuccess = () => (
  <Suspense fallback={null}>
    <LazyInPersonSuccess />
  </Suspense>
)

const LazyConfirmNewEmail = React.lazy(
  () => import("src/pages/confirm-new-email/confirm-new-email")
)
const ConfirmNewEmail = () => (
  <Suspense fallback={null}>
    <LazyConfirmNewEmail />
  </Suspense>
)

const LazyPatientViewerErrorBoundary = React.lazy(
  () => import("src/pages/epro/viewers/patient/patient-viewer-error-boundary")
)
const PatientViewerErrorBoundary = () => (
  <Suspense>
    <LazyPatientViewerErrorBoundary />
  </Suspense>
)
const LazyPatientViewer = React.lazy(
  () => import("src/pages/epro/viewers/patient/patient-viewer")
)
const PatientViewer = () => (
  <Suspense>
    <LazyPatientViewer />
  </Suspense>
)

const LazyTrainingViewer = React.lazy(
  () => import("src/pages/epro/viewers/training/training-viewer")
)
const TrainingViewer = () => (
  <Suspense>
    <LazyTrainingViewer />
  </Suspense>
)

const LazyPreviewTrainingViewer = React.lazy(
  () => import("src/pages/epro/viewers/training/preview-training-viewer")
)
const PreviewTrainingViewer = () => (
  <Suspense>
    <LazyPreviewTrainingViewer />
  </Suspense>
)

const LazyStudyTabScreenshot = React.lazy(
  () => import("src/pages/entry/study-screenshots/study-tab-screenshot")
)
const StudyTabScreenshot = () => (
  <Suspense>
    <LazyStudyTabScreenshot />
  </Suspense>
)
const LazyStudyDescriptionScreenshot = React.lazy(
  () => import("src/pages/entry/study-screenshots/study-description-screenshot")
)
const StudyDescriptionScreenshot = () => (
  <Suspense>
    <LazyStudyDescriptionScreenshot />
  </Suspense>
)

const LazyRedirectToAppstore = React.lazy(
  () => import("src/pages/redirect-to-appstore")
)
const RedirectToAppstore = () => (
  <Suspense>
    <LazyRedirectToAppstore />
  </Suspense>
)

const LazyTodo = React.lazy(() => import("src/pages/entry/todo/todo"))
const EntryTodo = () => (
  <Suspense fallback={null}>
    <SmartBanner />
    <LazyTodo />
  </Suspense>
)

const LazyAccount = React.lazy(() => import("src/pages/entry/account/account"))
const EntryAccount = () => (
  <Suspense fallback={null}>
    <LazyAccount />
  </Suspense>
)

const LazyAccountErrorBoundary = React.lazy(
  () => import("src/pages/entry/account/account-error-boundary")
)
const AccountErrorBoundary = () => (
  <Suspense fallback={null}>
    <LazyAccountErrorBoundary />
  </Suspense>
)

const LazyTodoErrorBoundary = React.lazy(
  () => import("src/pages/entry/todo/todo-error-boundary")
)
const TodoErrorBoundary = () => (
  <Suspense fallback={null}>
    <LazyTodoErrorBoundary />
  </Suspense>
)

const LazySettings = React.lazy(
  () => import("src/pages/entry/settings/settings")
)
const EntrySettings = () => (
  <Suspense fallback={null}>
    <LazySettings />
  </Suspense>
)

const LazySettingsErrorBoundary = React.lazy(
  () => import("src/pages/entry/settings/settings-error-boundary")
)
const SettingsErrorBoundary = () => (
  <Suspense fallback={null}>
    <LazySettingsErrorBoundary />
  </Suspense>
)
const LazyScreenshotErrorBoundary = React.lazy(
  () =>
    import("src/pages/epro/viewers/screenshot/epro-screenshot-error-boundary")
)
const EproScreenshotErrorBoundary = () => (
  <Suspense>
    <LazyScreenshotErrorBoundary />
  </Suspense>
)
const LazyScreenshotViewer = React.lazy(
  () => import("src/pages/epro/viewers/screenshot/epro-screenshot")
)
const EproScreenshot = () => (
  <Suspense>
    <LazyScreenshotViewer />
  </Suspense>
)

export const createEntryRoutes = (): RouteObject[] => [
  {
    path: routeNames.entry.route,
    element: (
      <AuthenticatedRoute>
        <Entry />
      </AuthenticatedRoute>
    ),
    children: [
      {
        path: routeNames.todo.route,
        Component: EntryTodo,
        errorElement: <TodoErrorBoundary />,
        handle: {
          headerTextId: "entry.todo",
          isMainTab: true,
        } satisfies EntryRouteHandle,
      },
      {
        path: routeNames.completed.route,
        errorElement: <TodoErrorBoundary />,
        lazy: () => import("src/pages/entry/todo/completed"),
      },
      createStudyRoutes(),
      {
        path: routeNames.econsentPdfViewer.route,
        lazy: () => import("src/pages/econsent-pdf/viewer/page"),
      },
      {
        path: routeNames.account.route,
        Component: EntryAccount,
        errorElement: <AccountErrorBoundary />,
        handle: {
          headerTextId: "entry.account",
          isMainTab: true,
        } satisfies EntryRouteHandle,
      },
      {
        path: routeNames.activateNewStudy.route,
        lazy: () => import("src/pages/entry/account/activate-new-study"),
        errorElement: <AccountErrorBoundary />,
      },
      {
        path: routeNames.activateNewStudyConfirmYob.route,
        lazy: () => import("src/pages/entry/account/account-confirm-yob"),
        errorElement: <AccountErrorBoundary />,
      },
      {
        path: routeNames.settings.route,
        Component: EntrySettings,
        handle: {
          defaultBackPath: routeNames.account(),
          headerTextId: "account.settings",
          isMainTab: false,
        } satisfies NestedRouteHandle & EntryRouteHandle,
        errorElement: <SettingsErrorBoundary />,
      },
      {
        path: routeNames.viewVisitFromTodos.route,
        lazy: () => import("src/pages/entry/todo/visit/visit-details"),
      },
      createRedirect({
        from: routeNames.DEPRECATED_econsentStudies.route,
        to: routeNames.todo.route,
      }),
      createRedirect({
        from: routeNames.DEPRECATED_econsentLibrary.route,
        to: routeNames.todo.route,
      }),
      createRedirect({
        index: true,
        to: routeNames.todo.route,
      }),
      {
        path: routeNames.entry.route,
        element: <NavigateWithParams to={routeNames.todo.route} />,
        children: [
          createRedirect({
            from: "*",
            to: routeNames.todo.route,
          }),
        ],
      },
    ],
  },
]

export const createEproViewerRoutes = (): RouteObject[] => {
  return [
    {
      path: routeNames.eproViewSurveyFromTodo.route,
      element: (
        <AuthenticatedRoute>
          <PatientViewer />
        </AuthenticatedRoute>
      ),
      errorElement: <PatientViewerErrorBoundary />,
      children: [
        {
          path: "*",
          element: (
            <AuthenticatedRoute>
              <PatientViewer />
            </AuthenticatedRoute>
          ),
        },
      ],
    },
    {
      path: routeNames.eproViewPreviewTrainingSurvey.route,
      element: <PreviewTrainingViewer />,
      errorElement: <PatientViewerErrorBoundary />,
      children: [
        {
          path: "*",
          element: <PreviewTrainingViewer />,
        },
      ],
    },
    {
      path: routeNames.eproViewTrainingSurvey.route,
      element: (
        <AuthenticatedRoute>
          <TrainingViewer />
        </AuthenticatedRoute>
      ),
      errorElement: <PatientViewerErrorBoundary />,
      children: [
        {
          path: "*",
          element: (
            <AuthenticatedRoute>
              <TrainingViewer />
            </AuthenticatedRoute>
          ),
        },
      ],
    },
    ...[
      createRedirect({
        from: routeNames.eproViewSurveyFromTasks.route,
        to: routeNames.eproViewSurveyFromTodo.route,
      }),
    ],
  ]
}

/**
 * For existing collection documents generated before the release of 24R1, their global preview links were generated
 * pointed to patients environments. There WAS a cloudfront configuration to automatically redirect those links to
 * sponsors, but now this logic will live in the patients codebase.
 */
const RedirectFromPatientsToSponsors = () => {
  React.useEffect(() => {
    const url = new URL(window.location.href)
    url.hostname = url.hostname.replace("patients", "sponsors")
    window.location.assign(url.toString())
  }, [])
  return null
}
export const routes: RouteObject[] = [
  {
    path: routeNames.DEPRECATED_eproPreviewV2.route,
    element: <RedirectFromPatientsToSponsors />,
  },
  {
    path: routeNames.DEPRECATED_eproPreviewV1.route,
    element: <RedirectFromPatientsToSponsors />,
  },
  {
    path: routeNames.login.route,
    Component: Login,
  },
  {
    path: routeNames.eproLibrarySurveyTemplateScreenshot.route,
    Component: EproScreenshot,
    errorElement: <EproScreenshotErrorBoundary />,
  },
  {
    path: routeNames.eproScreenshot.route,
    Component: EproScreenshot,
    errorElement: <EproScreenshotErrorBoundary />,
  },
  {
    path: routeNames.diaryDetailsScreenshot.route,
    lazy: () =>
      import("src/pages/entry/todo/diary/diary-details-collection-screenshot"),
    errorElement: <EproScreenshotErrorBoundary />,
  },
  {
    path: routeNames.libraryDiaryDetailsScreenshot.route,
    lazy: () =>
      import("src/pages/entry/todo/diary/diary-details-library-screenshot"),
    errorElement: <EproScreenshotErrorBoundary />,
  },
  {
    path: routeNames.confirmIdentity.route,
    lazy: () => import("src/pages/confirm-identity/confirm-identity"),
  },
  {
    path: routeNames.confirmIdentityEmail.route,
    lazy: () => import("src/pages/confirm-identity/confirm-identity-email"),
  },
  {
    path: routeNames.confirmIdentityPhone.route,
    lazy: () => import("src/pages/confirm-identity/confirm-identity-phone"),
  },
  {
    path: routeNames.identityConfirmed.route,
    lazy: () => import("src/pages/confirm-identity/identity-confirmed"),
  },
  {
    path: routeNames.version.route,
    lazy: () => import("src/pages/patients-version"),
  },
  {
    path: routeNames.error.route,
    Component: Error,
    children: [
      {
        path: "*",
        Component: Error,
      },
    ],
  },
  {
    path: routeNames.registerActivate.route,
    lazy: () => import("src/pages/register/register-activate"),
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-activate"),
      },
    ],
  },
  {
    path: routeNames.resetAccount.route,
    lazy: () => import("src/pages/register/reset-account"),
  },
  {
    path: routeNames.registerActivateRedirect.route,
    lazy: () => import("src/pages/register/register-activate-redirect"),
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-activate-redirect"),
      },
    ],
  },
  {
    path: routeNames.registerConfirmYob.route,
    lazy: () => import("src/pages/register/register-confirm-yob"),
    handle: {
      defaultBackPath: routeNames.registerActivate(),
    },
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-confirm-yob"),
      },
    ],
  },
  {
    path: routeNames.registerSetUsername.route,
    lazy: () => import("src/pages/register/register-set-username"),
    handle: {
      defaultBackPath: routeNames.registerActivate(),
    },
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-set-username"),
      },
    ],
  },
  {
    path: routeNames.registerSetPassword.route,
    lazy: () => import("src/pages/register/register-set-password"),
    handle: {
      defaultBackPath: routeNames.registerSetUsername(),
    },
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-set-password"),
      },
    ],
  },
  {
    path: routeNames.registerSetVerificationMethod.route,
    lazy: () => import("src/pages/register/register-set-verification-method"),
    handle: {
      defaultBackPath: routeNames.registerSetPassword(),
    },
    children: [
      {
        path: "*",
        lazy: () =>
          import("src/pages/register/register-set-verification-method"),
      },
    ],
  },
  {
    path: routeNames.registerSetVerificationMethodEmail.route,
    lazy: () => import("src/pages/register/register-set-email"),
    handle: {
      defaultBackPath: routeNames.registerSetVerificationMethod(),
    },
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-set-email"),
      },
    ],
  },
  {
    path: routeNames.registerSetVerificationMethodPhone.route,
    lazy: () => import("src/pages/register/register-set-phone"),
    handle: {
      defaultBackPath: routeNames.registerSetVerificationMethod(),
    },
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-set-phone"),
      },
    ],
  },
  {
    path: routeNames.registerConfirmEmail.route,
    lazy: () => import("src/pages/register/register-confirm-email"),
    handle: {
      defaultBackPath: routeNames.registerSetVerificationMethodEmail(),
    },
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-confirm-email"),
      },
    ],
  },
  {
    path: routeNames.registerConfirmPhone.route,
    lazy: () => import("src/pages/register/register-confirm-phone"),
    handle: {
      defaultBackPath: routeNames.registerSetVerificationMethodPhone(),
    },
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-confirm-phone"),
      },
    ],
  },
  {
    path: routeNames.registerLegacyConfirmIdentity.route,
    lazy: () => import("src/pages/confirm-identity/confirm-identity"),
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/confirm-identity/confirm-identity"),
      },
    ],
  },
  {
    path: routeNames.registerLegacyConfirmPhone.route,
    lazy: () => import("src/pages/register/register-confirm-phone"),
    handle: {
      defaultBackPath: routeNames.registerLegacyConfirmIdentity(),
    },
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-confirm-phone"),
      },
    ],
  },
  {
    path: routeNames.registerLegacySetUsername.route,
    lazy: () => import("src/pages/register/register-set-username"),
    handle: {
      defaultBackPath: routeNames.registerLegacyConfirmIdentity(),
    },
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-set-username"),
      },
    ],
  },
  {
    path: routeNames.registerLegacySetPassword.route,
    lazy: () => import("src/pages/register/register-set-password"),
    handle: {
      defaultBackPath: routeNames.registerLegacySetUsername(),
    },
    children: [
      {
        path: "*",
        lazy: () => import("src/pages/register/register-set-password"),
      },
    ],
  },
  {
    path: routeNames.enterMfa.route,
    Component: EnterMfa,
    children: [
      {
        path: "*",
        Component: EnterMfa,
      },
    ],
  },
  {
    path: routeNames.resetSvUserPassword.route,
    Component: ResetSvUserPassword,
    children: [
      {
        path: "*",
        Component: ResetSvUserPassword,
      },
    ],
  },
  {
    // Not in route names because this shouldn't be used elsewhere
    path: "/confirm-new-field",
    loader: ({ request }) => {
      const url = new URL(request.url)
      const field = url.searchParams.get("field")
      if (field === CONFIRM_FIELD_TYPES.phone) {
        url.pathname = routeNames.confirmNewNumber()
        return redirect(url.href)
      } else if (field === CONFIRM_FIELD_TYPES.email) {
        url.pathname = routeNames.confirmNewEmail()
        return redirect(url.href)
      } else {
        return null
      }
    },
    element: null,
    children: [
      {
        path: "*",
        element: null,
      },
    ],
  },
  {
    path: routeNames.studyTabScreenshot.route,
    Component: StudyTabScreenshot,
    handle: {
      headerTextId: "entry.study",
      isMainTab: true,
    } satisfies EntryRouteHandle,
  },
  {
    path: routeNames.studyDescriptionScreenshot.route,
    Component: StudyDescriptionScreenshot,
    handle: {
      headerTextId: "study.details",
      isMainTab: false,
    } satisfies EntryRouteHandle,
  },
  {
    path: routeNames.resourcesScreenshot.route,
    lazy: () =>
      import("src/pages/entry/study-screenshots/resources-screenshot"),
    handle: {
      headerTextId: "study.resources",
      isMainTab: false,
    } satisfies EntryRouteHandle,
  },
  {
    path: routeNames.confirmNewEmail.route,
    element: (
      <AuthenticatedRoute Fallback={ConfirmNewEmailExpired}>
        <ConfirmNewEmail />
      </AuthenticatedRoute>
    ),
    children: [
      {
        path: "*",
        element: (
          <AuthenticatedRoute Fallback={ConfirmNewEmailExpired}>
            <ConfirmNewEmail />
          </AuthenticatedRoute>
        ),
      },
    ],
  },
  {
    path: routeNames.patientsRedirect.route,
    lazy: () => import("src/pages/patients-redirect"),
  },
  {
    path: routeNames.confirmNewEmailExpired.route,
    Component: ConfirmNewEmailExpired,
  },
  {
    path: routeNames.forgotPassword.route,
    lazy: () => import("src/pages/forgot-password/forgot-password"),
    errorElement: <ForgotPasswordErrorBoundary />,
  },
  {
    path: routeNames.forgotPasswordEmailSent.route,
    lazy: () => import("src/pages/forgot-password/forgot-password-email-sent"),
    errorElement: <ForgotPasswordErrorBoundary />,
  },
  {
    path: routeNames.newPassword.route,
    lazy: () => import("src/pages/forgot-password/new-password"),
    errorElement: <ForgotPasswordErrorBoundary />,
  },
  {
    path: routeNames.newPasswordMfa.route,
    lazy: () => import("src/pages/forgot-password/new-password-mfa"),
    errorElement: <ForgotPasswordErrorBoundary />,
  },
  {
    path: routeNames.setNewPassword.route,
    lazy: () => import("src/pages/forgot-password/set-new-password"),
    errorElement: <ForgotPasswordErrorBoundary />,
  },
  {
    path: routeNames.determineUser.route,
    Component: DetermineUser,
    children: [
      {
        path: "*",
        Component: DetermineUser,
      },
    ],
  },
  {
    path: routeNames.help.route,
    Component: Help,
    children: [
      {
        path: "*",
        Component: Help,
      },
    ],
  },
  {
    path: routeNames.privacyNotice.route,
    Component: Pn,
    children: [
      {
        path: "*",
        Component: Pn,
      },
    ],
  },
  {
    path: routeNames.termsOfService.route,
    Component: Tos,
    children: [
      {
        path: "*",
        Component: Tos,
      },
    ],
  },
  ...createEconsentPdfRoutes(),
  { path: routeNames.econsentEditor.route, Component: EconsentEditor },
  {
    path: routeNames.econsentPreviewDocumentRoot.route,
    Component: EconsentViewer,
  },
  {
    path: routeNames.econsentPreviewDocument.route,
    Component: EconsentViewer,
  },
  {
    path: routeNames.econsentPreviewDocumentFromEditorRoot.route,
    Component: EconsentViewer,
  },
  {
    path: routeNames.econsentPreviewDocumentFromEditor.route,
    Component: EconsentViewer,
  },
  {
    path: routeNames.econsentPreviewGenerator.route,
    Component: EconsentPreviewGenerator,
  },
  { path: routeNames.econsentGenerate.route, Component: EconsentGenerate },
  { path: routeNames.inPersonConfirm.route, Component: InPersonConfirm },
  { path: routeNames.inPersonRoles.route, Component: InPersonRoles },
  { path: routeNames.redirectToAppstore.route, Component: RedirectToAppstore },
  { path: routeNames.inPersonSuccess.route, Component: InPersonSuccess },
  ...createEproViewerRoutes(),
  {
    path: routeNames.inPerson.route,
    element: (
      <AuthenticatedRoute Fallback={InPersonFallback}>
        <InPerson />
      </AuthenticatedRoute>
    ),
  },
  {
    path: routeNames.inPersonEconsentViewer.route,
    element: (
      <AuthenticatedRoute Fallback={InPersonFallback}>
        <EconsentViewer inPerson />
      </AuthenticatedRoute>
    ),
  },
  {
    path: routeNames.econsentViewDocumentFromStudy.route,
    element: (
      <AuthenticatedRoute>
        <EconsentViewer />
      </AuthenticatedRoute>
    ),
    handle: {
      defaultBackPath: studyRouteNames.consentForms(),
    } satisfies NestedRouteHandle,
    children: [
      {
        path: "*",
        element: (
          <AuthenticatedRoute>
            <EconsentViewer />
          </AuthenticatedRoute>
        ),
      },
    ],
  },
  {
    path: routeNames.econsentViewDocumentFromTodo.route,
    element: (
      <AuthenticatedRoute>
        <EconsentViewer />
      </AuthenticatedRoute>
    ),
    children: [
      {
        path: "*",
        element: (
          <AuthenticatedRoute>
            <EconsentViewer />
          </AuthenticatedRoute>
        ),
      },
    ],
  },
  {
    element: (
      <AuthenticatedRoute>
        <Outlet />
      </AuthenticatedRoute>
    ),
    errorElement: <PatientViewerErrorBoundary />,
    children: [
      {
        path: routeNames.viewDiaryDetails.route,
        lazy: () => import("src/pages/entry/todo/diary/diary-details"),
        handle: {
          defaultBackPath: routeNames.todo(),
        } satisfies NestedRouteHandle,
      },
      {
        path: routeNames.viewDiaryDetailsFromCompleted.route,
        lazy: () =>
          import("src/pages/entry/todo/diary/diary-details-completed"),
      },
      {
        path: routeNames.viewDiarySurvey.route,
        lazy: () =>
          import("src/pages/epro/viewers/diary/view-diary-from-details"),
      },
      {
        path: routeNames.viewDiarySurveyFromCompleted.route,
        lazy: () =>
          import("src/pages/epro/viewers/diary/view-diary-from-completed"),
      },
      {
        path: routeNames.viewDiarySurveyFromDetailsCompleted.route,
        lazy: () =>
          import("src/pages/epro/viewers/diary/view-diary-from-completed-details"),
      },
    ],
  },
  {
    path: routeNames.DEPRECATED_econsentViewDocument.route,
    element: (
      <NavigateWithLocation
        props={(location, params) => ({
          replace: true,
          to: {
            pathname: routeNames.econsentViewDocumentFromTasks({
              documentId: params.documentId as string,
            }),
            search: location.search,
          },
          state: {
            from: {
              pathname: routeNames.tasks(),
              search: location.search,
            },
          },
        })}
      />
    ),
  },
  {
    path: routeNames.DEPRECATED_econsentViewDocumentFromLibrary.route,
    element: (
      <NavigateWithLocation
        props={(location, params) => ({
          replace: true,
          to: {
            pathname: routeNames.econsentViewDocumentFromDocuments({
              documentId: params.documentId as string,
            }),
            search: location.search,
          },
          state: {
            from: {
              pathname: routeNames.documents(),
              search: location.search,
            },
          },
        })}
      />
    ),
    children: [
      {
        path: "*",
        element: (
          <NavigateWithLocation
            props={(location, params) => ({
              replace: true,
              to: {
                pathname: routeNames.econsentViewDocumentFromDocuments({
                  documentId: params.documentId as string,
                }),
                search: location.search,
              },
              state: {
                from: {
                  pathname: routeNames.documents(),
                  search: location.search,
                },
              },
            })}
          />
        ),
      },
    ],
  },
  ...[
    ...createEntryRoutes(),
    createRedirect({
      from: routeNames.econsentViewDocumentFromDocuments.route,
      to: routeNames.econsentViewDocumentFromStudy.route,
    }),
    createRedirect({
      from: routeNames.econsentViewDocumentFromTasks.route,
      to: routeNames.econsentViewDocumentFromTodo.route,
    }),
    createRedirect({
      from: routeNames.viewVisitFromVisits.route,
      to: routeNames.viewVisitFromTodos.route,
    }),
    createRedirect({
      from: routeNames.documents(),
      to: studyRouteNames.resources(),
    }),
  ],
  {
    index: true,
    loader: ({ request }) => {
      const url = new URL(request.url)
      url.pathname = isSessionValid() ? routeNames.entry() : routeNames.login()
      return redirect(url.href)
    },
  },
]
